import {
  Children,
  cloneElement,
  createContext,
  forwardRef,
  isValidElement,
  useCallback,
  useContext,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type MutableRefObject,
  type ReactElement,
  type ReactNode,
  type Ref,
  type SyntheticEvent,
} from 'react';
import { createPortal } from 'react-dom';
import {
  autoUpdate,
  computePosition,
  viewportSize,
  type Align,
  type PlacementOptions,
  type Side,
} from '../lib/position.js';

/**
 * The machinery under `popover.tsx` and `dropdown-menu.tsx`. Not exported from the package: the
 * public surface is the components in those two files, with the props Radix used to take.
 *
 * HOW IT WORKS. The content element carries the native `popover="auto"` attribute and is shown
 * with `showPopover()`, so it lives in the browser's TOP LAYER. That is what puts it above a
 * modal `<dialog>` (which is itself in the top layer, opened earlier) and above any z-index on the
 * page, and what gives it the platform's light-dismiss — a click elsewhere, Escape, Android's back
 * gesture — and its one-auto-popover-at-a-time rule.
 *
 * The same dismissals are ALSO handled here in script. Two reasons: a browser without the popover
 * API still gets a working menu, and an Escape that closes a popover must stop there rather than
 * also close the dialog underneath, which only a capture-phase listener that runs first can do.
 * Both paths end in the same `setOpen(false)`, so whichever runs second finds nothing to do.
 *
 * WHERE THE ELEMENT GOES. It is portalled into the nearest dialog (or open popover) around its
 * trigger, else into `<body>`. Inside a modal `<dialog>` everything outside the dialog is inert,
 * so a popover portalled to `<body>` could be seen but not clicked; inside the dialog's subtree
 * it is reachable, keeps the dialog's focus trap happy, and still escapes the dialog's overflow
 * because the top layer ignores it.
 */

// ── Small helpers ───────────────────────────────────────────────────────────────────────────────

type PossibleRef<T> = Ref<T> | undefined;

function setRef<T>(ref: PossibleRef<T>, value: T | null): void {
  if (typeof ref === 'function') ref(value);
  else if (ref) (ref as MutableRefObject<T | null>).current = value;
}

export function composeRefs<T>(...refs: PossibleRef<T>[]): (node: T | null) => void {
  return (node) => refs.forEach((ref) => setRef(ref, node));
}

export function useComposedRefs<T>(...refs: PossibleRef<T>[]): (node: T | null) => void {
  // The refs themselves are the dependencies: a new callback only when one of them changes.
  return useCallback(composeRefs(...refs), refs);
}

/** The caller's handler first; ours only if the caller did not `preventDefault()`. */
export function composeEventHandlers<E extends SyntheticEvent | Event>(
  theirs: ((event: E) => void) | undefined,
  ours: (event: E) => void,
): (event: E) => void {
  return (event) => {
    theirs?.(event);
    if (!event.defaultPrevented) ours(event);
  };
}

/**
 * `asChild`: render the one child element instead of our own, with our props merged onto it.
 * The child's own handlers run before ours, its className is appended to ours and its style wins.
 * A private stand-in for Radix's Slot, so these two components need nothing from Radix.
 */
export const Slot = forwardRef<HTMLElement, HTMLAttributes<HTMLElement> & { children?: ReactNode }>(
  function Slot({ children, ...slotProps }, forwardedRef) {
    const child = Children.only(children);
    if (!isValidElement(child)) return null;
    const element = child as ReactElement<Record<string, unknown>> & { ref?: Ref<HTMLElement> };
    const childProps = element.props;
    const merged: Record<string, unknown> = { ...slotProps, ...childProps };
    for (const key of Object.keys(childProps)) {
      const slotValue = (slotProps as Record<string, unknown>)[key];
      const childValue = childProps[key];
      if (
        /^on[A-Z]/.test(key) &&
        typeof slotValue === 'function' &&
        typeof childValue === 'function'
      ) {
        merged[key] = (...args: unknown[]) => {
          const result = (childValue as (...a: unknown[]) => unknown)(...args);
          (slotValue as (...a: unknown[]) => unknown)(...args);
          return result;
        };
      } else if (key === 'style') {
        merged[key] = { ...(slotValue as object), ...(childValue as object) };
      } else if (key === 'className') {
        merged[key] = [slotValue, childValue].filter(Boolean).join(' ');
      }
    }
    merged.ref = forwardedRef ? composeRefs(forwardedRef, element.ref) : element.ref;
    return cloneElement(element, merged);
  },
);

function useControllableState<T>(
  prop: T | undefined,
  defaultProp: T,
  onChange: ((value: T) => void) | undefined,
): [T, MutableRefObject<T>, (next: T) => void] {
  const [uncontrolled, setUncontrolled] = useState(defaultProp);
  const controlled = prop !== undefined;
  const value = controlled ? prop : uncontrolled;

  // Written during render so a handler that fires before the next commit (a browser `toggle`
  // event, a cleanup) reads the value that is about to be on screen, not the one from the last
  // effect run.
  const valueRef = useRef(value);
  valueRef.current = value;
  const controlledRef = useRef(controlled);
  controlledRef.current = controlled;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const setValue = useCallback((next: T) => {
    if (!controlledRef.current) setUncontrolled(next);
    if (next !== valueRef.current) onChangeRef.current?.(next);
  }, []);

  return [value, valueRef, setValue];
}

// ── Context ─────────────────────────────────────────────────────────────────────────────────────

interface PopoverContextValue {
  open: boolean;
  /** The open state as of the render being committed; see `useControllableState`. */
  openRef: MutableRefObject<boolean>;
  /** Stable for the life of the root. */
  setOpen: (open: boolean) => void;
  modal: boolean;
  contentId: string;
  triggerRef: MutableRefObject<HTMLElement | null>;
  /** Set by `PopoverAnchor`; when present the content is positioned against it, not the trigger. */
  anchorRef: MutableRefObject<HTMLElement | null>;
  /**
   * True when the close came from a click or focus OUTSIDE. Focus is then left where the person
   * put it rather than dragged back to the trigger.
   */
  dismissedOutsideRef: MutableRefObject<boolean>;
}

const PopoverContext = createContext<PopoverContextValue | null>(null);

export function usePopoverContext(consumer: string): PopoverContextValue {
  const context = useContext(PopoverContext);
  if (!context) throw new Error(`${consumer} must be rendered inside its root component`);
  return context;
}

export interface PopoverRootProps {
  children?: ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * Accepted for compatibility with the Radix API. A modal popover used to trap focus and block
   * the page; here every popover behaves as a non-modal one (closes on a click or focus outside).
   */
  modal?: boolean;
}

export function PopoverRoot({
  children,
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  modal = false,
}: PopoverRootProps): ReactElement {
  const [open, openRef, setOpen] = useControllableState(openProp, defaultOpen, onOpenChange);
  const contentId = useId();
  const triggerRef = useRef<HTMLElement | null>(null);
  const anchorRef = useRef<HTMLElement | null>(null);
  const dismissedOutsideRef = useRef(false);

  const value = useMemo<PopoverContextValue>(
    () => ({
      open,
      openRef,
      setOpen,
      modal,
      contentId,
      triggerRef,
      anchorRef,
      dismissedOutsideRef,
    }),
    [open, openRef, setOpen, modal, contentId],
  );
  return <PopoverContext.Provider value={value}>{children}</PopoverContext.Provider>;
}

// ── Trigger ─────────────────────────────────────────────────────────────────────────────────────

export type TriggerProps = ButtonHTMLAttributes<HTMLButtonElement> & { asChild?: boolean };

export const PopoverTriggerImpl = forwardRef<HTMLButtonElement, TriggerProps>(
  function PopoverTriggerImpl(
    { asChild, onPointerDown, onKeyDown, onClick, ...props },
    forwardedRef,
  ) {
    const context = usePopoverContext('PopoverTrigger');
    const ref = useComposedRefs<HTMLElement>(forwardedRef as Ref<HTMLElement>, context.triggerRef);
    // Whether the popover was open when the pointer went down on the trigger. The browser's
    // light dismiss may close it between pointerdown and click; without this, that click would
    // read "closed" and open it straight back up.
    const openAtPointerDownRef = useRef<boolean | null>(null);
    const Comp = (asChild ? Slot : 'button') as 'button';

    return (
      <Comp
        type={asChild ? undefined : 'button'}
        aria-haspopup="dialog"
        aria-expanded={context.open}
        aria-controls={context.open ? context.contentId : undefined}
        data-state={context.open ? 'open' : 'closed'}
        {...props}
        ref={ref}
        onPointerDown={composeEventHandlers(onPointerDown, () => {
          openAtPointerDownRef.current = context.openRef.current;
        })}
        onKeyDown={composeEventHandlers(onKeyDown, () => {
          // A click that follows a key press has no pointer behind it.
          openAtPointerDownRef.current = null;
        })}
        onClick={composeEventHandlers(onClick, () => {
          const wasOpen = openAtPointerDownRef.current ?? context.openRef.current;
          openAtPointerDownRef.current = null;
          context.setOpen(!wasOpen);
        })}
      />
    );
  },
);

// ── Content ─────────────────────────────────────────────────────────────────────────────────────

export type PointerDownOutsideEvent = CustomEvent<{ originalEvent: PointerEvent }>;
export type FocusOutsideEvent = CustomEvent<{ originalEvent: FocusEvent }>;

export interface ContentProps extends HTMLAttributes<HTMLDivElement> {
  asChild?: boolean;
  side?: Side;
  sideOffset?: number;
  align?: Align;
  alignOffset?: number;
  /** Flip to the other side and slide along the edge to stay inside the viewport. Default on. */
  avoidCollisions?: boolean;
  collisionPadding?: number | Partial<Record<Side, number>>;
  /** Keep the content mounted (hidden) while closed. */
  forceMount?: true;
  onOpenAutoFocus?: (event: Event) => void;
  onCloseAutoFocus?: (event: Event) => void;
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
  onPointerDownOutside?: (event: PointerDownOutsideEvent) => void;
  onFocusOutside?: (event: FocusOutsideEvent) => void;
  onInteractOutside?: (event: PointerDownOutsideEvent | FocusOutsideEvent) => void;
  /** Accepted for compatibility with the Radix API; the viewport is always the boundary. */
  collisionBoundary?: Element | null | Array<Element | null>;
  /** Accepted for compatibility with the Radix API; there is no arrow. */
  arrowPadding?: number;
  /** Accepted for compatibility with the Radix API; the content always slides partially. */
  sticky?: 'partial' | 'always';
  /** Accepted for compatibility with the Radix API; has no effect. */
  hideWhenDetached?: boolean;
  /** Accepted for compatibility with the Radix API; the position is always kept up to date. */
  updatePositionStrategy?: 'optimized' | 'always';
}

/**
 * Escape closes the TOPMOST open popover only. One capture listener on `window` runs before
 * anything else on the page — including a dialog's own Escape handling — so a popover inside a
 * dialog closes alone and the dialog stays open.
 */
interface Layer {
  escape: (event: KeyboardEvent) => void;
}
const layers: Layer[] = [];

function onWindowKeyDown(event: KeyboardEvent): void {
  if (event.key !== 'Escape' || event.isComposing) return;
  layers[layers.length - 1]?.escape(event);
}

function pushLayer(layer: Layer): () => void {
  if (layers.length === 0) window.addEventListener('keydown', onWindowKeyDown, true);
  layers.push(layer);
  return () => {
    const index = layers.indexOf(layer);
    if (index !== -1) layers.splice(index, 1);
    if (layers.length === 0) window.removeEventListener('keydown', onWindowKeyDown, true);
  };
}

function isShowing(element: HTMLElement): boolean {
  try {
    return element.matches(':popover-open');
  } catch {
    return false;
  }
}

function supportsPopover(element: HTMLElement): boolean {
  return typeof element.showPopover === 'function';
}

function showInTopLayer(element: HTMLElement, source: HTMLElement | null): void {
  if (!supportsPopover(element) || isShowing(element)) return;
  try {
    // `source` makes the trigger part of the popover for light dismiss where it is supported
    // (a click on the trigger is then not a click outside); older browsers ignore the argument.
    (element.showPopover as (options?: { source?: HTMLElement }) => void)(
      source ? { source } : undefined,
    );
  } catch {
    // Not connected, or no longer a popover: nothing to show, and nothing a caller could do.
  }
}

function hideFromTopLayer(element: HTMLElement): void {
  if (!supportsPopover(element) || !isShowing(element)) return;
  try {
    element.hidePopover();
  } catch {
    // Already gone.
  }
}

const TABBABLE =
  'button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

/**
 * Focus the first tabbable thing inside, skipping links, else the box itself. Links are skipped
 * the way Radix skipped them: landing on a link is rarely what opening a panel was for.
 */
function focusFirst(container: HTMLElement): void {
  const first = Array.from(container.querySelectorAll<HTMLElement>(TABBABLE)).find(
    (element) => element.tagName !== 'A',
  );
  (first ?? container).focus({ preventScroll: true });
}

function portalContainerFor(node: HTMLElement | null): HTMLElement {
  return (
    node?.closest<HTMLElement>('[popover], dialog, [role="dialog"], [role="alertdialog"]') ??
    document.body
  );
}

/**
 * The unstyled content box: positioning, top layer, dismissal and focus. `PopoverContent` and
 * `DropdownMenuContent` both render it with their own look.
 */
export const PopoverContentImpl = forwardRef<HTMLDivElement, ContentProps>(
  function PopoverContentImpl({ forceMount, ...props }, forwardedRef) {
    const context = usePopoverContext('PopoverContent');
    const [container, setContainer] = useState<HTMLElement | null>(null);
    const present = context.open || forceMount === true;

    useLayoutEffect(() => {
      if (!present) return;
      setContainer(portalContainerFor(context.anchorRef.current ?? context.triggerRef.current));
    }, [present, context.anchorRef, context.triggerRef]);

    if (!present || !container) return null;
    return createPortal(<ContentBox ref={forwardedRef} {...props} />, container);
  },
);

const POPOVER_ATTRIBUTE: Record<string, string> = { popover: 'auto' };

const ContentBox = forwardRef<HTMLDivElement, Omit<ContentProps, 'forceMount'>>(function ContentBox(
  {
    asChild,
    side = 'bottom',
    sideOffset = 0,
    align = 'center',
    alignOffset = 0,
    avoidCollisions = true,
    collisionPadding = 0,
    onOpenAutoFocus,
    onCloseAutoFocus,
    onEscapeKeyDown,
    onPointerDownOutside,
    onFocusOutside,
    onInteractOutside,
    collisionBoundary: _collisionBoundary,
    arrowPadding: _arrowPadding,
    sticky: _sticky,
    hideWhenDetached: _hideWhenDetached,
    updatePositionStrategy: _updatePositionStrategy,
    style,
    ...props
  },
  forwardedRef,
) {
  const context = usePopoverContext('PopoverContent');
  const contentRef = useRef<HTMLDivElement | null>(null);
  const ref = useComposedRefs(forwardedRef, contentRef);

  // The newest props, for listeners that live as long as the popover is open.
  const latest = useRef({
    placement: {} as PlacementOptions,
    onOpenAutoFocus,
    onCloseAutoFocus,
    onEscapeKeyDown,
    onPointerDownOutside,
    onFocusOutside,
    onInteractOutside,
  });
  latest.current = {
    placement: { side, sideOffset, align, alignOffset, avoidCollisions, collisionPadding },
    onOpenAutoFocus,
    onCloseAutoFocus,
    onEscapeKeyDown,
    onPointerDownOutside,
    onFocusOutside,
    onInteractOutside,
  };

  const { open } = context;
  useLayoutEffect(() => {
    const element = contentRef.current;
    if (!open || !element) return;
    const anchorOf = () => context.anchorRef.current ?? context.triggerRef.current;

    showInTopLayer(element, context.triggerRef.current);

    const reposition = () => {
      const anchor = anchorOf();
      if (!anchor) return;
      const placement = computePosition(
        anchor.getBoundingClientRect(),
        { width: element.offsetWidth, height: element.offsetHeight },
        viewportSize(),
        latest.current.placement,
      );
      element.style.top = `${placement.top}px`;
      element.style.left = `${placement.left}px`;
      element.dataset.side = placement.side;
      element.dataset.align = placement.align;
    };
    reposition();
    const anchor = anchorOf();
    const stopUpdating = anchor ? autoUpdate(anchor, element, reposition) : () => undefined;

    context.dismissedOutsideRef.current = false;
    const openFocus = new CustomEvent('popover.openAutoFocus', { cancelable: true });
    latest.current.onOpenAutoFocus?.(openFocus);
    if (!openFocus.defaultPrevented) focusFirst(element);

    const isInside = (target: EventTarget | null) =>
      target instanceof Node &&
      (element.contains(target) || context.triggerRef.current?.contains(target) === true);

    const dismissOutside = () => {
      context.dismissedOutsideRef.current = true;
      context.setOpen(false);
    };

    const onPointerDown = (event: PointerEvent) => {
      if (isInside(event.target)) return;
      const outside: PointerDownOutsideEvent = new CustomEvent('popover.pointerDownOutside', {
        cancelable: true,
        detail: { originalEvent: event },
      });
      latest.current.onPointerDownOutside?.(outside);
      latest.current.onInteractOutside?.(outside);
      if (!outside.defaultPrevented) dismissOutside();
    };

    const onFocusIn = (event: FocusEvent) => {
      if (isInside(event.target)) return;
      const outside: FocusOutsideEvent = new CustomEvent('popover.focusOutside', {
        cancelable: true,
        detail: { originalEvent: event },
      });
      latest.current.onFocusOutside?.(outside);
      latest.current.onInteractOutside?.(outside);
      if (!outside.defaultPrevented) dismissOutside();
    };

    // The browser closed it on its own (light dismiss, the back gesture, another auto popover
    // opening): bring React's state into line.
    const onToggle = () => {
      if (!isShowing(element) && context.openRef.current) context.setOpen(false);
    };

    const popLayer = pushLayer({
      escape: (event) => {
        latest.current.onEscapeKeyDown?.(event);
        if (event.defaultPrevented) return;
        // Stop here: the dialog underneath must not see this Escape, and a canceled keydown
        // is not turned into a close request for the next thing in the top layer either.
        event.preventDefault();
        event.stopPropagation();
        context.setOpen(false);
      },
    });
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('focusin', onFocusIn);
    element.addEventListener('toggle', onToggle);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('focusin', onFocusIn);
      element.removeEventListener('toggle', onToggle);
      popLayer();
      stopUpdating();

      // `openRef` is false only for a real close. React's StrictMode also runs this cleanup
      // on mount, and a popover being unmounted with the rest of its screen is not a close:
      // neither moves focus, and neither hides the element — hiding would make the browser
      // put focus back where it was before the popover opened, and StrictMode's re-run of the
      // effect would then find it there. An element that is unmounted leaves the top layer
      // with it.
      if (!context.openRef.current) {
        const active = document.activeElement;
        const focusWasInside = !active || active === document.body || element.contains(active);
        const closeFocus = new CustomEvent('popover.closeAutoFocus', { cancelable: true });
        latest.current.onCloseAutoFocus?.(closeFocus);
        if (
          !closeFocus.defaultPrevented &&
          !context.dismissedOutsideRef.current &&
          focusWasInside
        ) {
          context.triggerRef.current?.focus();
        }
        hideFromTopLayer(element);
      }
    };
  }, [open, context]);

  const Comp = (asChild ? Slot : 'div') as 'div';
  return (
    <Comp
      id={context.contentId}
      role="dialog"
      tabIndex={-1}
      data-state={open ? 'open' : 'closed'}
      data-side={side}
      data-align={align}
      hidden={open ? undefined : true}
      {...POPOVER_ATTRIBUTE}
      {...props}
      ref={ref}
      // The top layer and `position: fixed` make these viewport coordinates; `top` and `left`
      // are written by the positioning effect, after the box can be measured.
      style={{ position: 'fixed', right: 'auto', bottom: 'auto', margin: 0, ...style }}
    />
  );
});
